/**
 * SellerDiscounts - Seller Discount & Promotion Management
 * Allows sellers to create percentage/fixed discounts for their products
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Tag,
  Plus,
  Percent,
  DollarSign,
  Calendar,
  Trash2,
  Edit,
  Check,
  X,
  Loader2,
  Gift,
  Clock,
  ShoppingBag,
  Copy,
  Share2
} from 'lucide-react';

export default function SellerDiscounts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  
  // Form state
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountName, setDiscountName] = useState('');
  const [description, setDescription] = useState('');
  const [minPurchase, setMinPurchase] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [applyToAll, setApplyToAll] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [discountsRes, productsRes] = await Promise.all([
        api.get('/seller/discounts').catch(() => ({ data: [] })),
        api.get('/merchant/products').catch(() => ({ data: [] }))
      ]);
      setDiscounts(discountsRes.data || []);
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setDiscountCode(code);
  };

  const resetForm = () => {
    setDiscountType('percentage');
    setDiscountValue('');
    setDiscountCode('');
    setDiscountName('');
    setDescription('');
    setMinPurchase('');
    setMaxUses('');
    setStartDate('');
    setEndDate('');
    setSelectedProducts([]);
    setApplyToAll(true);
    setEditingDiscount(null);
  };

  const handleSave = async () => {
    if (!discountValue || !discountCode || !discountName) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: discountName,
        code: discountCode.toUpperCase(),
        type: discountType,
        value: parseFloat(discountValue),
        description,
        min_purchase: minPurchase ? parseFloat(minPurchase) : null,
        max_uses: maxUses ? parseInt(maxUses) : null,
        start_date: startDate || null,
        end_date: endDate || null,
        apply_to_all: applyToAll,
        product_ids: applyToAll ? [] : selectedProducts
      };

      if (editingDiscount) {
        await api.put(`/seller/discounts/${editingDiscount.id}`, data);
        toast.success('Discount updated!');
      } else {
        await api.post('/seller/discounts', data);
        toast.success('Discount created!');
      }

      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this discount?')) return;
    try {
      await api.delete(`/seller/discounts/${id}`);
      toast.success('Discount deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setDiscountType(discount.type);
    setDiscountValue(discount.value.toString());
    setDiscountCode(discount.code);
    setDiscountName(discount.name);
    setDescription(discount.description || '');
    setMinPurchase(discount.min_purchase?.toString() || '');
    setMaxUses(discount.max_uses?.toString() || '');
    setStartDate(discount.start_date?.split('T')[0] || '');
    setEndDate(discount.end_date?.split('T')[0] || '');
    setApplyToAll(discount.apply_to_all);
    setSelectedProducts(discount.product_ids || []);
    setShowCreateDialog(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const getDiscountStatus = (discount) => {
    const now = new Date();
    const start = discount.start_date ? new Date(discount.start_date) : null;
    const end = discount.end_date ? new Date(discount.end_date) : null;
    
    if (start && now < start) return { label: 'Scheduled', color: 'bg-blue-500' };
    if (end && now > end) return { label: 'Expired', color: 'bg-gray-500' };
    if (discount.max_uses && discount.uses_count >= discount.max_uses) return { label: 'Exhausted', color: 'bg-gray-500' };
    return { label: 'Active', color: 'bg-green-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/business')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Tag className="w-5 h-5 text-[#FFD300]" />
                  Discounts & Promotions
                </h1>
                <p className="text-sm text-slate-400">Create discounts for your products</p>
              </div>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#00A86B] hover:bg-[#00A86B]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Discount
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#FFD300]" />
                    {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Discount Name */}
                  <div>
                    <Label>Discount Name *</Label>
                    <Input
                      value={discountName}
                      onChange={(e) => setDiscountName(e.target.value)}
                      placeholder="Summer Sale 20% Off"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>

                  {/* Discount Code */}
                  <div>
                    <Label>Discount Code *</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="SUMMER20"
                        className="bg-slate-800 border-slate-700 font-mono"
                      />
                      <Button variant="outline" onClick={generateCode} className="border-slate-700">
                        Generate
                      </Button>
                    </div>
                  </div>

                  {/* Discount Type & Value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type *</Label>
                      <Select value={discountType} onValueChange={setDiscountType}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="percentage">
                            <span className="flex items-center gap-2">
                              <Percent className="w-4 h-4" />
                              Percentage Off
                            </span>
                          </SelectItem>
                          <SelectItem value="fixed">
                            <span className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              Fixed Amount
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Value *</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(e.target.value)}
                          placeholder={discountType === 'percentage' ? '20' : '500'}
                          className="bg-slate-800 border-slate-700 pl-8"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                          {discountType === 'percentage' ? '%' : 'J$'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description (shown to customers)</Label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Get 20% off on all products this summer!"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>

                  {/* Min Purchase */}
                  <div>
                    <Label>Minimum Purchase (optional)</Label>
                    <Input
                      type="number"
                      value={minPurchase}
                      onChange={(e) => setMinPurchase(e.target.value)}
                      placeholder="1000"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave empty for no minimum</p>
                  </div>

                  {/* Max Uses */}
                  <div>
                    <Label>Maximum Uses (optional)</Label>
                    <Input
                      type="number"
                      value={maxUses}
                      onChange={(e) => setMaxUses(e.target.value)}
                      placeholder="100"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited uses</p>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-slate-800 border-slate-700 mt-1"
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-slate-800 border-slate-700 mt-1"
                      />
                    </div>
                  </div>

                  {/* Apply to Products */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <ShoppingBag className="w-4 h-4" />
                      Apply to Products
                    </Label>
                    <div className="flex gap-4 mb-3">
                      <button
                        onClick={() => setApplyToAll(true)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          applyToAll ? 'border-[#00A86B] bg-[#00A86B]/10' : 'border-slate-700'
                        }`}
                      >
                        All Products
                      </button>
                      <button
                        onClick={() => setApplyToAll(false)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          !applyToAll ? 'border-[#00A86B] bg-[#00A86B]/10' : 'border-slate-700'
                        }`}
                      >
                        Select Products
                      </button>
                    </div>
                    
                    {!applyToAll && products.length > 0 && (
                      <div className="max-h-48 overflow-y-auto space-y-2 bg-slate-800/50 rounded-lg p-3">
                        {products.map(product => (
                          <div
                            key={product.id}
                            onClick={() => toggleProduct(product.id)}
                            className={`p-2 rounded-lg cursor-pointer flex items-center gap-3 ${
                              selectedProducts.includes(product.id) 
                                ? 'bg-[#00A86B]/20 border border-[#00A86B]' 
                                : 'hover:bg-slate-700'
                            }`}
                          >
                            <div className="w-10 h-10 rounded bg-slate-700 overflow-hidden">
                              {product.images?.[0] && (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium truncate">{product.title}</p>
                              <p className="text-xs text-[#00A86B]">J${product.price?.toLocaleString()}</p>
                            </div>
                            {selectedProducts.includes(product.id) && (
                              <Check className="w-5 h-5 text-[#00A86B]" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-[#00A86B] hover:bg-[#00A86B]/90"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {editingDiscount ? 'Update Discount' : 'Create Discount'}
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {discounts.length === 0 ? (
          <Card className="p-12 bg-slate-800/50 border-slate-700 text-center">
            <Gift className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Discounts Yet</h3>
            <p className="text-slate-400 mb-6">Create your first discount to attract more customers!</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Discount
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {discounts.map(discount => {
              const status = getDiscountStatus(discount);
              return (
                <Card key={discount.id} className="p-6 bg-slate-800/50 border-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">{discount.name}</h3>
                        <Badge className={`${status.color} text-white`}>{status.label}</Badge>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-slate-700 rounded-lg font-mono text-[#FFD300]">
                          {discount.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(discount.code)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          {discount.type === 'percentage' ? (
                            <><Percent className="w-4 h-4" />{discount.value}% off</>
                          ) : (
                            <><DollarSign className="w-4 h-4" />J${discount.value} off</>
                          )}
                        </span>
                        
                        {discount.min_purchase && (
                          <span>Min: J${discount.min_purchase.toLocaleString()}</span>
                        )}
                        
                        {discount.max_uses && (
                          <span>{discount.uses_count || 0}/{discount.max_uses} uses</span>
                        )}
                        
                        {discount.end_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Expires {new Date(discount.end_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      {discount.description && (
                        <p className="text-sm text-slate-500">{discount.description}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(discount)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(discount.id)}
                        className="text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
